import { useEffect } from 'react';
import { useRouter } from 'next/router';
import { useQuery } from '@tanstack/react-query';
import {
	actionRequiredService,
	caseApplicationsService,
	GET_ACTION_REQUIRED,
	GET_CASE_APPLICATIONS,
	GET_CASES_FILES,
	GET_CLIENT_APPROVE_STATUS,
	lawyerDirectoryService,
} from '@/api';
import AppRoutes from '@/const/routes';
import { useEventSource } from '@/hooks/useEventSource';
import { ClientCaseDetailsQueries } from '@/interfaces';

export const useClientCaseDetails = (
	id?: string | string[]
): ClientCaseDetailsQueries => {
	const router = useRouter();

	const { data: stepNotification } = useEventSource({
		url: `${process.env.NEXT_PUBLIC_API}/sse/action-resolved/subscribe`,
	});

	const firstParsed = stepNotification ? JSON.parse(stepNotification) : null;
	const parsedNotification = firstParsed ? JSON.parse(firstParsed.data) : null;

	const notCaseId = parsedNotification?.caseId;
	const date = parsedNotification?.date;

	const {
		data: actionRequired,
		refetch: refetchActionRequired,
		error: actionRequiredError,
	} = useQuery({
		queryKey: [GET_ACTION_REQUIRED, id],
		queryFn: () => actionRequiredService.getActionRequired(id as string),
		gcTime: 0,
		enabled: !!id,
		retry: 3,
		retryDelay: 500,
	});

	const {
		data: caseApplications,
		refetch: refetchCase,
		error: errorCaseApplications,
	} = useQuery({
		queryKey: [GET_CASE_APPLICATIONS, id, actionRequired],
		queryFn: () => caseApplicationsService.getCaseById(id as string),
		gcTime: 0,
		retry: false,
		enabled: !!id && !!actionRequired,
	});

	const {
		data: filesApplications,
		refetch: refetchDocuments,
		isError: isDocumentsError,
	} = useQuery({
		queryKey: [GET_CASES_FILES, caseApplications],
		queryFn: () =>
			actionRequiredService.getActionRequiredFiles(
				caseApplications?.data.application_id || 0
			),
		gcTime: 0,
		enabled: !!caseApplications,
	});

	const { data: clientApproveStatus, refetch: refetchStatus } = useQuery({
		queryKey: [GET_CLIENT_APPROVE_STATUS, caseApplications],
		queryFn: () =>
			lawyerDirectoryService.getClientApproveStatus(
				caseApplications?.data.application_id
			),
		enabled: !!caseApplications?.data.application_id,
	});

	useEffect(() => {
		if (
			(errorCaseApplications &&
				(errorCaseApplications as any).status === 404) ||
			actionRequiredError
		) {
			router.push(AppRoutes.CLIENTAPP_DASHBOARD);
		}
	}, [errorCaseApplications, actionRequiredError]);

	useEffect(() => {
		if (notCaseId === id) {
			refetchActionRequired();
			refetchDocuments();
			refetchStatus();
			refetchCase();
		}
	}, [date, notCaseId]);

	const clientApproveEmail =
		!clientApproveStatus?.data?.isApproved &&
		clientApproveStatus?.data?.emailSent;

	return {
		actionRequired,
		refetchActionRequired,
		caseApplications,
		refetchCase,
		filesApplications,
		refetchDocuments,
		isDocumentsError,
		clientApproveStatus,
		refetchStatus,
		clientApproveEmail,
	};
};
